import * as React from 'react'
import ReactSelect from 'react-select'
import styled from 'styled-components'
import * as constants from '../../static/constants'
import moment from 'moment'

interface IProps {
  name: string
  defaultDate?: moment.Moment | string | null
  label?: string | null
  error?: string | null
  required?: boolean
  disabled?: boolean
  minYear?: number
  maxYear?: number
  onChangeHandler: (date: moment.Moment | null) => void
  customStyles?: any
}

const ReactSelectDateInput: React.FC<IProps> = props => {
  const currentYear = moment().year()
  const minYear = props.minYear ?? currentYear
  const maxYear = props.maxYear ?? currentYear + 50

  // 初期値のパース
  const initialDate = React.useMemo(() => {
    if (!props.defaultDate) {
      return null
    }
    if (typeof props.defaultDate === 'string') {
      return moment(props.defaultDate)
    }
    return props.defaultDate
  }, [props.defaultDate])

  const defaultValues = initialDate
    ? {
        year: initialDate.year(),
        month: initialDate.month() + 1,
        day: initialDate.date(),
      }
    : {
        year: null,
        month: null,
        day: null,
      }

  const [year, setYear] = React.useState<number | null>(defaultValues.year)
  const [month, setMonth] = React.useState<number | null>(defaultValues.month)
  const [day, setDay] = React.useState<number | null>(defaultValues.day)

  // 月に応じた日数の最大値を計算（月が選択されている場合のみ）
  const maxDays = React.useMemo(() => {
    if (!year || !month) {
      return 31
    } // デフォルト値
    return moment(`${year}-${month}`, 'YYYY-MM').daysInMonth()
  }, [year, month])

  const availableDays = React.useMemo(() => {
    return Array.from({ length: maxDays }, (_, i) => i + 1)
  }, [maxDays])

  // 日が変更された場合、最大日数を超えている場合は調整
  React.useEffect(() => {
    if (day && day > maxDays) {
      setDay(maxDays)
    }
  }, [maxDays, day])

  // 年・月・日の配列を生成（空の選択肢を追加）
  const years = React.useMemo(() => {
    const yearOptions = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i)
    return [
      { value: null, label: '' },
      ...yearOptions.map(yearValue => ({
        value: yearValue,
        label: String(yearValue),
      })),
    ]
  }, [minYear, maxYear])

  const months = React.useMemo(() => {
    const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1)
    return [
      { value: null, label: '' },
      ...monthOptions.map(monthValue => ({
        value: monthValue,
        label: String(monthValue),
      })),
    ]
  }, [])

  const days = React.useMemo(() => {
    const dayOptions = Array.from({ length: maxDays }, (_, i) => i + 1)
    return [
      { value: null, label: '' },
      ...dayOptions.map(dayValue => ({
        value: dayValue,
        label: String(dayValue),
      })),
    ]
  }, [maxDays])

  // 日付変更時に親コンポーネントに通知
  React.useEffect(() => {
    if (year && month && day) {
      const date = moment(`${year}-${month}-${day}`, 'YYYY-M-D')
      if (date.isValid()) {
        props.onChangeHandler(date)
      } else {
        props.onChangeHandler(null)
      }
    } else {
      props.onChangeHandler(null)
    }
  }, [year, month, day])

  const defaultSelectStyles = {
    control: (provided, _) => ({
      ...provided,
      border: `1px solid ${constants.COLORS.divider}`,
      borderRadius: 4,
      '&:hover': { border: `1px solid ${constants.COLORS.primary}` },
    }),
  }

  const selectStyles = props.customStyles || defaultSelectStyles

  return (
    <ReactSelectDateInputWrapper>
      {props.label && (
        <LabelWrapper>
          {props.label}
          {props.required && <RequiredMark>*</RequiredMark>}
        </LabelWrapper>
      )}
      <DateSelectWrapper>
        <DateSelectItem>
          <ReactSelect
            placeholder="選択"
            options={years}
            isMulti={false}
            isDisabled={props.disabled}
            isClearable={false}
            onChange={option => setYear(option?.value ?? null)}
            value={
              year !== null
                ? {
                    value: year,
                    label: String(year),
                  }
                : null
            }
            menuPortalTarget={document.querySelector('body')}
            styles={selectStyles}
          />
          <DateLabel>年</DateLabel>
        </DateSelectItem>
        <DateSelectItem>
          <ReactSelect
            placeholder="選択"
            options={months}
            isMulti={false}
            isDisabled={props.disabled}
            isClearable={false}
            onChange={option => setMonth(option?.value ?? null)}
            value={
              month !== null
                ? {
                    value: month,
                    label: String(month),
                  }
                : null
            }
            menuPortalTarget={document.querySelector('body')}
            styles={selectStyles}
          />
          <DateLabel>月</DateLabel>
        </DateSelectItem>
        <DateSelectItem>
          <ReactSelect
            placeholder="選択"
            options={days}
            isMulti={false}
            isDisabled={props.disabled}
            isClearable={false}
            onChange={option => setDay(option?.value ?? null)}
            value={
              day !== null
                ? {
                    value: day,
                    label: String(day),
                  }
                : null
            }
            menuPortalTarget={document.querySelector('body')}
            styles={selectStyles}
          />
          <DateLabel>日</DateLabel>
        </DateSelectItem>
      </DateSelectWrapper>
      {props.error && <ErrorText>{props.error}</ErrorText>}
    </ReactSelectDateInputWrapper>
  )
}

const ReactSelectDateInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const LabelWrapper = styled.div`
  font-size: 15px;
  font-weight: bold;
  margin-bottom: 8px;
`

const RequiredMark = styled.span`
  color: ${constants.COLORS.caution};
  margin-left: 4px;
`

const DateSelectWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
    gap: 12px;
  }
`

const DateSelectItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;

  > div:first-child {
    flex: 1;
  }

  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    width: 100%;
  }
`

const DateLabel = styled.span`
  font-size: 15px;
  font-weight: normal;
  color: ${constants.COLORS.black};
  white-space: nowrap;
`

const ErrorText = styled.span`
  display: block;
  margin-top: 8px;
  color: ${constants.COLORS.cautionText};
  font-size: 14px;
`

export default ReactSelectDateInput
